/** @format */

import React, { useState, useEffect } from "react";
import { getAPIURL } from "../../services/helper";
import http from "../../services/http";
import {
  Spinner,
  Container,
  Card,
  Input,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import classnames from "classnames";
import Swal from "sweetalert2";
import { handlePrinting } from "../../Utils/PrintingCart";
import InvoiceModal from "./components/InvoiceModal";
import TransactionTable from "./components/TransactionTable";
import { useDispatch } from "react-redux";
import { openView } from "../../ReduxToolkit/Reducers/viewReducer";

const TransactionReports = ({ backTo = null }) => {
  const api_url = getAPIURL();
  const dispatch = useDispatch();

  const [data, setData] = useState({ pos: [], tealbo: [] });
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [activeTab, setActiveTab] = useState("pos");
  const [viewModal, setViewModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    fetchTransactionReports();
  }, []);

  const fetchTransactionReports = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateRange.start) params.append("start_date", dateRange.start);
      if (dateRange.end) params.append("end_date", dateRange.end);

      const { data } = await http.get(
        `${api_url}/reports/transactions?${params.toString()}`
      );
      setData(data);
    } catch (error) {
      console.error("Error fetching transaction reports:", error);
    }
    setLoading(false);
  };

  // Action Handlers
  const handleRefund = async (row) => {
    const { value: formValues } = await Swal.fire({
      title: "Process Refund",
      html: `
        <input id="refund-amount" class="swal2-input" type="number" placeholder="Refund amount" max="${row.order_total}">
        <input id="refund-reason" class="swal2-input" type="text" placeholder="Refund reason">
        <small>Max refund: ${row.order_total}</small>
      `,
      showCancelButton: true,
      confirmButtonText: "Process Refund",
      preConfirm: () => {
        const amount = document.getElementById("refund-amount").value;
        const reason = document.getElementById("refund-reason").value;

        if (!amount || amount <= 0) {
          Swal.showValidationMessage("Please enter a valid amount");
          return false;
        }
        if (parseFloat(amount) > parseFloat(row.order_total)) {
          Swal.showValidationMessage("Refund amount cannot exceed order total");
          return false;
        }
        if (!reason.trim()) {
          Swal.showValidationMessage("Please enter a refund reason");
          return false;
        }

        return { amount: parseFloat(amount), reason: reason.trim() };
      },
    });

    if (formValues) {
      try {
        const { data } = await http.post(`${api_url}/refund-order`, {
          order_id: row.order_id,
          refund_amount: formValues.amount,
          refund_reason: formValues.reason,
        });
        if (data.success) {
          Swal.fire("Success!", "Refund processed successfully", "success");
          fetchTransactionReports();
        }
      } catch (error) {
        Swal.fire("Error!", "Failed to process refund", "error");
        console.error("Error processing refund:", error);
      }
    }
  };

  const handleDelete = async (row) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Delete Order ID: ${row.order_id}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const { data } = await http.post(`${api_url}/delete-order`, {
          order_id: row.order_id,
        });
        if (data.success) {
          fetchTransactionReports();
        }
      } catch (error) {
        console.error("Error deleting order:", error);
      }
    }
  };

  const handleView = (row) => {
    setSelectedOrder(row);
    setViewModal(true);
  };

  const handlePrintReceipt = (row) => {
    handlePrinting(row, { receipt: true, label: false });
  };

  const handlePrintLabel = (row) => {
    handlePrinting(row, { receipt: false, label: true });
  };

  return (
    <div className="page-body">
      <Container className="mt-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          {backTo && (
            <Button color="primary" onClick={() => dispatch(openView(backTo))}>
              Back
            </Button>
          )}
          <h2 className="text-center flex-grow-1">Transaction Reports</h2>
          <div></div>
        </div>

        {/* Date Range Filters */}
        <div className="d-flex mb-3">
          <Input
            type="date"
            value={dateRange.start}
            onChange={(e) =>
              setDateRange({ ...dateRange, start: e.target.value })
            }
            className="me-2"
          />
          <Input
            type="date"
            value={dateRange.end}
            onChange={(e) =>
              setDateRange({ ...dateRange, end: e.target.value })
            }
            className="me-2"
          />
          <Button
            color="primary"
            onClick={fetchTransactionReports}
            className="me-2"
          >
            Filter
          </Button>
          <Button
            color="info"
            onClick={() => {
              setDateRange({ start: "", end: "" });
              fetchTransactionReports();
            }}
          >
            Reset
          </Button>
        </div>

        {/* Tabs for POS and Tealbo Transactions */}
        <Nav tabs>
          <NavItem>
            <NavLink
              role="button"
              className={classnames({ active: activeTab === "pos" })}
              onClick={() => setActiveTab("pos")}
            >
              POS Transactions
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              role="button"
              className={classnames({ active: activeTab === "tealbo" })}
              onClick={() => setActiveTab("tealbo")}
            >
              Tealbo Transactions
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={activeTab}>
          <TabPane tabId="pos">
            {loading ? (
              <div className="text-center">
                <Spinner color="primary" size="lg" />
                <p>Loading Transactions...</p>
              </div>
            ) : (
              <Card className="p-3 shadow-sm">
                <TransactionTable
                  data={data.pos}
                  onView={handleView}
                  onDelete={handleDelete}
                />
              </Card>
            )}
          </TabPane>

          <TabPane tabId="tealbo">
            {loading ? (
              <div className="text-center">
                <Spinner color="primary" size="lg" />
                <p>Loading Transactions...</p>
              </div>
            ) : (
              <Card className="p-3 shadow-sm">
                <TransactionTable
                  data={data.tealbo}
                  onView={handleView}
                  onDelete={handleDelete}
                />
              </Card>
            )}
          </TabPane>
        </TabContent>

        <InvoiceModal
          isOpen={viewModal}
          toggle={() => setViewModal(false)}
          selectedOrder={selectedOrder}
          onPrintReceipt={handlePrintReceipt}
          onPrintLabel={handlePrintLabel}
          onRefund={fetchTransactionReports}
          onDelete={handleDelete}
        />


      </Container>
    </div>
  );
};

export default TransactionReports;
